<template>
  <v-dialog
    :model-value="showDialog"
    persistent
    max-width="1200"
    :fullscreen="$vuetify.display.smAndDown"
    @update:model-value="$emit('update:showDialog', $event)"
    class="change-package-dialog"
  >
    <v-card>
      <v-card-title class="pa-0">
        <v-toolbar
          color="primary"
          dark
          flat
        >
          <v-toolbar-title class="main-title font-weight-bold">{{
            $t('product-packages.main-title')
          }}</v-toolbar-title>
          <v-spacer />
          <v-toolbar-items>
            <v-btn
              icon
              theme="dark"
              @click="$emit('update:showDialog', false)"
            >
              <v-icon
                size="xx-large"
                class="font-weight-bold"
                >mdi-close</v-icon
              >
            </v-btn>
          </v-toolbar-items>
        </v-toolbar>
      </v-card-title>

      <v-window
        v-model="step"
        class="change-package-content"
        disabled
      >
        <!-- Product Package Step -->
        <v-window-item value="product-package">
          <v-card-text class="d-flex justify-center text-align-center pt-0 px-0 pt-md-6 mb-6">
            <ProductPackages
              :show-currency-selector="false"
              :company="company"
              @change-package="handlePackageChange"
              style="min-height: 1200px"
            />
          </v-card-text>
        </v-window-item>

        <!-- Payment Step -->
        <v-window-item value="payment">
          <PaymentUpgrade
            v-if="step === 'payment'"
            :product-type="productType"
            :is-yearly="isYearly"
            :currency="currency"
            @back="step = 'product-package'"
          />
        </v-window-item>

        <!-- Downgrade Step -->
        <v-window-item value="downgrade">
          <Downgrade
            v-if="step === 'downgrade'"
            :product-type="productType"
            :is-yearly="isYearly"
            :currency="currency"
            @back="step = 'product-package'"
            @success="handleDowngradeSuccess"
          />
        </v-window-item>
      </v-window>
    </v-card>
  </v-dialog>
</template>

<script>
import { ProductType } from '@/lib/productTypes'
import { billwerkApi } from '@/modules/billwerk/lib'
import PaymentUpgrade from '@/modules/payment/PaymentUpgrade.vue'
import ProductPackages from '@/modules/productPackages/index.vue'
import SELF_SERVICE_PORTAL_URL from '@/modules/settings/accountSettings/queries/SelfServicePortalUrl.gql'

import Downgrade from './Downgrade.vue'

export default {
  name: 'ChangePackage',

  components: {
    ProductPackages,
    PaymentUpgrade,
    Downgrade,
  },

  props: {
    company: {
      type: Object,
      default: () => ({}),
    },
    showDialog: {
      type: Boolean,
      default: false,
    },
    defaultProductType: {
      type: String,
      default: '',
    },
  },

  emits: ['update:showDialog', 'downgrade'],

  data() {
    return {
      step: 'product-package',
      isYearly: true,
      currency: '',
      productType: '',
    }
  },

  watch: {
    showDialog(val) {
      if (val) {
        this.step = 'product-package'
      }
    },
  },

  mounted() {
    if (this.defaultProductType) {
      this.productType = this.defaultProductType
      this.step = 'payment'
    }
    const productType = this.$route.query.productType
    if (ProductType[productType]) {
      this.handlePackageChange({
        productType,
        isYearly: this.isYearly,
        currency: this.currency,
      })
    }
  },

  methods: {
    handleDowngradeSuccess() {
      this.$emit('update:showDialog', false)
      this.$emit('downgrade')
    },

    handlePackageChange({ productType, isYearly, currency }) {
      this.productType = productType
      this.isYearly = isYearly
      this.currency = currency
      this.step = {
        BEGINNER: 'downgrade',
        ADVANCED: 'payment',
      }[productType]
    },
  },

  apollo: {
    token: {
      query: SELF_SERVICE_PORTAL_URL,
      async update(data) {
        const token = data.billingSelfServicePortalDetails?.Token
        if (token) {
          const { Contract } = await billwerkApi.getContractDetails(token)
          this.currency = Contract.Currency
        }
      },
    },
  },
}
</script>

<style scoped>
.change-package-dialog {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.change-package-dialog :deep(.v-card) {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.change-package-content {
  flex: 1;
  overflow-y: auto;
}

/* Remove default Vuetify window transitions that might cause layout issues */
:deep(.v-window__container) {
  height: auto !important;
}

:deep(.v-window-item) {
  height: auto !important;
}

:deep(.v-window) {
  height: auto !important;
}

.main-title {
  font-weight: 500;
}
</style>
